/**
 * Identity: who is signed in, and how the credential is obtained.
 *
 * Two implementations behind one interface. Which one is constructed is decided once, at
 * boot, from configuration -- not per request.
 *
 * The development identity exists so the whole authorisation path, including hierarchy
 * scoping, can be exercised without a tenant. It is guarded in three places: configuration
 * validation refuses it outside local, the factory refuses to build it outside local, and
 * the implementation itself refuses to issue a credential outside local. Three guards for
 * one rule, because the failure mode is an authentication bypass in production.
 */

import type { Environment, RuntimeConfig } from '../config/runtime'

export const ROLES = [
  'developer',
  'manager',
  'governance-admin',
  'platform-admin',
  'executive',
] as const
export type Role = (typeof ROLES)[number]

/** Human labels for the role switcher and for prose. */
export const ROLE_LABELS: Readonly<Record<Role, string>> = {
  developer: 'Developer',
  manager: 'Development Manager',
  'governance-admin': 'Governance Administrator',
  'platform-admin': 'Platform Administrator',
  executive: 'Executive',
}

export interface Identity {
  readonly objectId: string
  readonly displayName: string
  readonly roles: readonly Role[]
  readonly workerId?: string
  readonly isDevelopmentIdentity: boolean
}

export type AuthStatus =
  'initialising' | 'authenticated' | 'anonymous' | 'error'

export interface Authenticator {
  readonly mode: 'entra' | 'development'
  /** Resolve the current identity, or undefined when nobody is signed in. */
  initialise(): Promise<Identity | undefined>
  /** Begin an interactive sign-in. */
  signIn(): Promise<Identity | undefined>
  signOut(): Promise<void>
  /** The Authorization header value for an API call, or undefined when anonymous. */
  credential(): Promise<string | undefined>
}

export function hasRole(
  identity: Identity | undefined,
  ...roles: readonly Role[]
): boolean {
  if (identity === undefined) return false
  return roles.some((role) => identity.roles.includes(role))
}

function encode(payload: unknown): string {
  const json = JSON.stringify(payload)
  const base64 = btoa(String.fromCharCode(...new TextEncoder().encode(json)))
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

const STORAGE_KEY = 'devex.developmentIdentity'

/**
 * A clearly-labelled local identity with a selectable role.
 *
 * Deliberately not a token. There is no signature to forge because there is no signature,
 * and the API refuses the whole scheme outside a local environment.
 */
export class DevelopmentAuthenticator implements Authenticator {
  readonly mode = 'development' as const

  private readonly environment: Environment
  private current: Identity | undefined

  constructor(config: RuntimeConfig) {
    if (config.environment !== 'local') {
      throw new Error(
        `The development identity may only be constructed in a local environment, ` +
          `received "${config.environment}".`,
      )
    }
    this.environment = config.environment
  }

  async initialise(): Promise<Identity | undefined> {
    const stored = this.read()
    this.current = stored
    return stored
  }

  async signIn(): Promise<Identity | undefined> {
    // Signing in with no role chosen would be indistinguishable from a real sign-in, which
    // is the confusion the visible switcher exists to prevent.
    return this.current
  }

  async signOut(): Promise<void> {
    this.current = undefined
    window.localStorage.removeItem(STORAGE_KEY)
  }

  /** Adopt a role. Local only; the switcher is the only caller. */
  assume(roles: readonly Role[], workerId?: string): Identity {
    const identity: Identity = {
      objectId: `dev-${roles.join('-')}`,
      displayName: roles.map((role) => ROLE_LABELS[role]).join(', '),
      roles,
      ...(workerId === undefined ? {} : { workerId }),
      isDevelopmentIdentity: true,
    }
    this.current = identity
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ roles, workerId: workerId ?? null }),
    )
    return identity
  }

  async credential(): Promise<string | undefined> {
    if (this.environment !== 'local') {
      throw new Error(
        'The development identity is refused outside a local environment.',
      )
    }
    if (this.current === undefined) return undefined
    return `DevIdentity ${encode({
      roles: this.current.roles,
      objectId: this.current.objectId,
      ...(this.current.workerId === undefined
        ? {}
        : { workerId: this.current.workerId }),
    })}`
  }

  private read(): Identity | undefined {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (raw === null) return undefined
    try {
      const parsed = JSON.parse(raw) as { roles?: unknown; workerId?: unknown }
      const roles = Array.isArray(parsed.roles)
        ? parsed.roles.filter((role): role is Role =>
            (ROLES as readonly unknown[]).includes(role),
          )
        : []
      if (roles.length === 0) return undefined
      const workerId =
        typeof parsed.workerId === 'string' ? parsed.workerId : undefined
      return {
        objectId: `dev-${roles.join('-')}`,
        displayName: roles.map((role) => ROLE_LABELS[role]).join(', '),
        roles,
        ...(workerId === undefined ? {} : { workerId }),
        isDevelopmentIdentity: true,
      }
    } catch {
      // A corrupt stored value is discarded rather than repaired: guessing at an identity
      // is exactly the wrong thing for an auth layer to do.
      window.localStorage.removeItem(STORAGE_KEY)
      return undefined
    }
  }
}

/**
 * Enterprise single sign-on.
 *
 * The provider library is loaded lazily, so a local run never pays for it and never needs
 * the configuration it requires.
 */
type MsalAccount = {
  homeAccountId?: string
  localAccountId?: string
  name?: string
  username?: string
  idTokenClaims?: Record<string, unknown>
}

type MsalClient = {
  loginRedirect: (request: unknown) => Promise<void>
  handleRedirectPromise: () => Promise<{ account?: MsalAccount } | null>
  getAllAccounts: () => MsalAccount[]
  setActiveAccount: (account: MsalAccount | null) => void
  acquireTokenSilent: (request: unknown) => Promise<{ accessToken: string }>
  acquireTokenRedirect: (request: unknown) => Promise<void>
  logoutRedirect: () => Promise<void>
  initialize: () => Promise<void>
}

export class EntraAuthenticator implements Authenticator {
  readonly mode = 'entra' as const

  private readonly config: RuntimeConfig
  private client: MsalClient | undefined
  private creating: Promise<MsalClient> | undefined
  private account: MsalAccount | undefined

  constructor(config: RuntimeConfig) {
    if (config.auth.tenantId === '' || config.auth.clientId === '') {
      throw new Error('Enterprise sign-in requires a tenantId and a clientId.')
    }
    this.config = config
  }

  private async instance(): Promise<MsalClient> {
    if (this.client !== undefined) return this.client
    if (this.creating === undefined) {
      this.creating = this.createClient()
    }
    try {
      this.client = await this.creating
      return this.client
    } catch (error) {
      this.creating = undefined
      throw error
    }
  }

  private async createClient(): Promise<MsalClient> {
    const { PublicClientApplication } = await import('@azure/msal-browser')
    const application = new PublicClientApplication({
      auth: {
        clientId: this.config.auth.clientId,
        authority: `https://login.microsoftonline.com/${this.config.auth.tenantId}`,
        redirectUri: window.location.origin,
        navigateToLoginRequestUrl: false,
      },
      cache: { cacheLocation: 'sessionStorage' },
    })
    await application.initialize()
    return application as unknown as MsalClient
  }

  private mappedRoles(raw: unknown): Role[] {
    const values = Array.isArray(raw) ? raw : []
    // Unrecognised role claims are discarded rather than carried. Rendering navigation for
    // a role the API does not know would promise the user something every route refuses.
    return values
      .map((value) => String(value))
      .map((value) => CLAIM_TO_ROLE[value])
      .filter((role): role is Role => role !== undefined)
  }

  private rolesFromAccessToken(accessToken: string): Role[] {
    const segment = accessToken.split('.')[1]
    if (segment === undefined || segment === '') return []
    try {
      const padded = segment.replace(/-/g, '+').replace(/_/g, '/')
      const json = atob(padded + '='.repeat((4 - (padded.length % 4)) % 4))
      const payload = JSON.parse(json) as { roles?: unknown }
      return this.mappedRoles(payload.roles)
    } catch {
      return []
    }
  }

  private toIdentity(
    account: MsalAccount,
    accessRoles: readonly Role[] = [],
  ): Identity {
    const fromIdToken = this.mappedRoles(account.idTokenClaims?.roles)
    const roles = accessRoles.length > 0 ? [...accessRoles] : fromIdToken
    return {
      objectId: account.localAccountId ?? account.homeAccountId ?? '',
      displayName: account.name ?? account.username ?? 'Signed in',
      roles,
      isDevelopmentIdentity: false,
    }
  }

  private async hydrate(
    application: MsalClient,
    account: MsalAccount,
  ): Promise<Identity> {
    this.account = account
    application.setActiveAccount(account)
    try {
      const result = await application.acquireTokenSilent({
        scopes: [this.config.auth.apiScope],
        account,
      })
      return this.toIdentity(
        account,
        this.rolesFromAccessToken(result.accessToken),
      )
    } catch {
      return this.toIdentity(account)
    }
  }

  async initialise(): Promise<Identity | undefined> {
    const application = await this.instance()
    const redirected = await application.handleRedirectPromise()
    const account = redirected?.account ?? application.getAllAccounts()[0]
    if (account === undefined) return undefined
    return this.hydrate(application, account)
  }

  async signIn(): Promise<Identity | undefined> {
    const application = await this.instance()
    const existing = this.account ?? application.getAllAccounts()[0]
    if (existing !== undefined) {
      return this.hydrate(application, existing)
    }
    await application.loginRedirect({ scopes: [this.config.auth.apiScope] })
    return undefined
  }

  async signOut(): Promise<void> {
    const application = await this.instance()
    await application.logoutRedirect()
  }

  async credential(): Promise<string | undefined> {
    if (this.account === undefined) return undefined
    const application = await this.instance()
    try {
      const result = await application.acquireTokenSilent({
        scopes: [this.config.auth.apiScope],
        account: this.account,
      })
      return `Bearer ${result.accessToken}`
    } catch {
      // Silent renewal failing means consent or session expiry, both of which need the
      // user. Returning undefined here would surface as a generic error instead.
      await application.acquireTokenRedirect({
        scopes: [this.config.auth.apiScope],
      })
      return undefined
    }
  }
}

/** Application role claims, mapped to the closed role set. */
const CLAIM_TO_ROLE: Readonly<Record<string, Role>> = {
  'DevEx.Developer': 'developer',
  'DevEx.User': 'developer',
  'DevEx.Manager': 'manager',
  'DevEx.GovernanceAdmin': 'governance-admin',
  'DevEx.Admin': 'platform-admin',
  'DevEx.Executive': 'executive',
}

/**
 * Choose the authenticator once, from configuration.
 *
 * Enterprise sign-in wins whenever it is configured, even locally, so a developer with a
 * tenant exercises the real path. The development identity requires all of: a local
 * environment, the flag set, and no enterprise configuration.
 */
export function buildAuthenticator(config: RuntimeConfig): Authenticator {
  if (config.auth.tenantId !== '' && config.auth.clientId !== '') {
    return new EntraAuthenticator(config)
  }
  if (config.environment === 'local' && config.auth.allowDevIdentity) {
    return new DevelopmentAuthenticator(config)
  }
  throw new Error(
    'No authenticator can be constructed. Configure enterprise sign-in, or run locally ' +
      'with the development identity enabled.',
  )
}
